import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, mediaUrl, formatMoney } from '@/lib/api';
import { useCategories } from '@/context/CategoriesContext';
import { useAuth } from '@/context/AuthContext';
import { usePromptLogin } from '@/context/LoginModalContext';
import { ChatDrawer } from '@/components/common/ChatDrawer';
import { useShareConfig } from '@/context/ShareConfigContext';
import { useShare } from '@/hooks/useShare';
import { SharePanel } from '@/components/common/SharePanel';
import { PublicHeader } from '@/components/marketplace/PublicHeader';
import { SEOMeta } from '@/components/common/SEOMeta';

import { AdCard } from '@/components/marketplace/AdCard';
import { RatingStars } from '@/components/marketplace/RatingStars';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { QRCodeSVG } from 'qrcode.react';
import { CheckCircle2, Share2, QrCode, Copy, MapPin, CalendarDays, Star, Package, MessageCircle, UserPlus, UserCheck, Users, ShoppingBag, Award, Truck, Clock } from 'lucide-react';
import { toast } from 'sonner';

const SocialIcon = ({ children }) => (
    <span className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-white text-black hover:scale-110 transition-transform shadow-lg">{children}</span>
);

export default function PublicUserPage() {
    const { nickname } = useParams();
    const { role, user: me } = useAuth();
    const promptLogin = usePromptLogin();
    const [chatOpen, setChatOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [qrOpen, setQrOpen] = useState(false);
    const [following, setFollowing] = useState(false);
    const [q, setQ] = useState('');
    const [category, setCategory] = useState('all');
    const [condition, setCondition] = useState('all');
    const [sort, setSort] = useState('recent');
    const cats = useCategories();
    const [hasStorefront, setHasStorefront] = useState(false);
    const { buildShareUrl } = useShareConfig();
    const [ratingOpen, setRatingOpen] = useState(false);
    const [eligibleOrders, setEligibleOrders] = useState([]);
    const [selectedOrderId, setSelectedOrderId] = useState('');
    const [ratingStars, setRatingStars] = useState(5);
    const [ratingComment, setRatingComment] = useState('');
    const [ratingBusy, setRatingBusy] = useState(false);
    const [timingStats, setTimingStats] = useState(null);

    const publicUrl = buildShareUrl(`/u/${nickname}`);

    useEffect(() => {
        if (!data || !me || me.id === data?.user?.id || role !== 'user') return;
        api.get('/me/orders/purchases').then(({ data: orders }) => {
            const eligible = (orders || []).filter(o =>
                (o.seller_id === data.user.id || o.seller?.id === data.user.id) &&
                ['paid', 'delivered', 'released'].includes(o.status)
            );
            setEligibleOrders(eligible);
            if (eligible.length > 0) setSelectedOrderId(eligible[0].id);
        }).catch(() => {});
    }, [data, me, role]);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api.get(`/profiles/${nickname}`),
            api.get(`/storefront/${nickname}`).catch(() => ({ data: null })),
        ]).then(([p, sf]) => {
            setData(p.data);
            setFollowing(!!p.data.is_following);
            setHasStorefront(!!(sf.data?.storefront?.enabled));
            // Load timing stats after profile loads
            if (p.data?.user?.id) {
                api.get(`/users/${p.data.user.id}/timing-stats`).then(({ data: ts }) => setTimingStats(ts)).catch(() => {});
            }
        }).finally(() => setLoading(false));
    }, [nickname]);

    const toggleFollow = async () => {
        if (role !== 'user') {
            const ok = await promptLogin({ intent: 'comment' });
            if (!ok) return;
        }
        try {
            const { data: r } = await api.post('/follow', { target_user_id: data.user.id });
            setFollowing(r.following);
            setData(d => ({ ...d, user: { ...d.user, followers_count: (d.user.followers_count || 0) + (r.following ? 1 : -1) } }));
        } catch { toast.error('Error'); }
    };

    const submitUserRating = async () => {
        if (!selectedOrderId) { toast.error('Selecciona una compra'); return; }
        setRatingBusy(true);
        try {
            await api.post('/ratings', { order_id: selectedOrderId, ratee_id: data.user.id, stars: ratingStars, comment: ratingComment || null });
            toast.success('¡Calificación publicada!');
            setRatingOpen(false);
            setRatingComment('');
            setRatingStars(5);
            const p = await api.get(`/profiles/${nickname}`);
            setData(p.data);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al publicar'); }
        finally { setRatingBusy(false); }
    };

    const copyLink = async () => {
        try { await navigator.clipboard.writeText(publicUrl); toast.success('Enlace copiado'); } catch { toast.error('No se pudo copiar'); }
    };

    const { share: shareNative, panelOpen: shareNativePanelOpen, closePanel: closeShareNativePanel } = useShare(publicUrl, `@${nickname} en Ok te lo vendo`);

    const socialShare = (platform) => {
        const text = encodeURIComponent(`Mira el perfil de @${nickname} en Ok te lo vendo`);
        const u = encodeURIComponent(publicUrl);
        const urls = {
            whatsapp: `https://wa.me/?text=${text}%20${u}`,
            twitter: `https://twitter.com/intent/tweet?text=${text}&url=${u}`,
            facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
            telegram: `https://t.me/share/url?url=${u}&text=${text}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
            email: `mailto:?subject=${text}&body=${text}%20${u}`,
        };
        window.open(urls[platform], '_blank', 'noopener,noreferrer');
    };

    const filteredAds = (data?.ads || []).filter(a => {
        if (q && !a.title.toLowerCase().includes(q.toLowerCase())) return false;
        if (category !== 'all' && a.category_slug !== category) return false;
        if (condition !== 'all' && a.condition !== condition) return false;
        return true;
    });
    const sorted = [...filteredAds].sort((a, b) => {
        if (sort === 'price_asc') return a.price - b.price;
        if (sort === 'price_desc') return b.price - a.price;
        if (sort === 'popular') return (b.views || 0) - (a.views || 0);
        return new Date(b.created_at) - new Date(a.created_at);
    });

    if (loading || !data) {
        return (
            <div className="min-h-screen flex flex-col">
                <PublicHeader />
                <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6">
                    <Skeleton className="h-64 rounded-3xl" />
                </main>
            </div>
        );
    }

    const u = data.user;
    const country = u.country || 'mx';
    const flag = `https://flagcdn.com/w80/${country.toLowerCase()}.png`;
    const joinDate = u.created_at ? new Date(u.created_at).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }) : '';

    const seoDesc = u.bio
        ? `${u.bio.slice(0, 120)} — Vendedor en Ok lo Vendo.`
        : `@${u.nickname} vende en Ok lo Vendo. ${data.ads?.length || 0} anuncios activos.`;

    return (
        <div className="min-h-screen flex flex-col">
            <SEOMeta
                title={`@${u.nickname}`}
                description={seoDesc}
                image={u.avatar_url || undefined}
                url={buildShareUrl(`/u/${nickname}`)}
                type="profile"
            />
            <PublicHeader />
            <main className="flex-1">
                {/* Hero */}
                <section className="relative profile-hero text-white overflow-hidden">
                    <div className="absolute inset-0 opacity-30" style={{ backgroundImage: u.cover_url ? `url(${u.cover_url})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' }} />
                    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-20 text-center">
                        {/* Profile picture centered */}
                        <div className="mx-auto mb-4 h-28 w-28 sm:h-36 sm:w-36 rounded-full bg-white p-1 shadow-2xl">
                            {u.avatar_url ? (
                                <img src={u.avatar_url} alt={u.nickname} className="h-full w-full rounded-full object-cover" />
                            ) : (
                                <div className="h-full w-full rounded-full bg-red-600 flex items-center justify-center text-4xl font-bold text-white">{u.nickname[0].toUpperCase()}</div>
                            )}
                        </div>
                        <div className="flex items-center justify-center gap-2">
                            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="public-profile-nickname">@{u.nickname}</h1>
                            {u.verified && <Badge className="bg-white text-black border-0 gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Verificado</Badge>}
                        </div>
                        {u.full_name && <p className="mt-1 text-white/90">{u.full_name}</p>}
                        {u.bio && <p className="mt-3 max-w-lg mx-auto text-sm sm:text-base text-white/85">{u.bio}</p>}

                        {/* Stats row */}
                        <div className="mt-6 flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-sm">
                            <div className="inline-flex items-center gap-2" data-testid="profile-stat-rating">
                                <RatingStars value={u.rating_avg} size="md" />
                                <span className="tabular-nums font-semibold">{u.rating_avg?.toFixed(1) || '0.0'}</span>
                                <span className="text-white/70">({u.rating_count})</span>
                            </div>
                            <span className="opacity-40">•</span>
                            <div className="inline-flex items-center gap-1.5" data-testid="profile-stat-sales"><Package className="h-4 w-4" />{u.total_sales} ventas</div>
                            <span className="opacity-40">•</span>
                            <div className="inline-flex items-center gap-1.5" data-testid="profile-stat-followers"><Users className="h-4 w-4" />{u.followers_count || 0} seguidores</div>
                            <span className="opacity-40">•</span>
                            <div className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" />Desde {joinDate}</div>
                            {u.location && (<>
                                <span className="opacity-40">•</span>
                                <div className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{u.location}</div>
                            </>)}
                            <span className="opacity-40">•</span>
                            <div className="inline-flex items-center gap-1.5">
                                <img src={flag} alt={country} className="h-4 w-6 object-cover rounded-sm" />
                                <span className="uppercase">{country}</span>
                            </div>
                        </div>

                        {/* Share buttons */}
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
                            <button onClick={() => socialShare('whatsapp')} data-testid="share-whatsapp" aria-label="WhatsApp">
                                <SocialIcon><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/></svg></SocialIcon>
                            </button>
                            <button onClick={() => socialShare('twitter')} data-testid="share-twitter" aria-label="X/Twitter">
                                <SocialIcon><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg></SocialIcon>
                            </button>
                            <button onClick={() => socialShare('facebook')} data-testid="share-facebook" aria-label="Facebook">
                                <SocialIcon><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 011.141.195v3.325a8.623 8.623 0 00-.653-.036 26.805 26.805 0 00-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 00-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647z"/></svg></SocialIcon>
                            </button>
                            <button onClick={() => socialShare('telegram')} data-testid="share-telegram" aria-label="Telegram">
                                <SocialIcon><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.464.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg></SocialIcon>
                            </button>
                            <button onClick={() => socialShare('linkedin')} data-testid="share-linkedin" aria-label="LinkedIn">
                                <SocialIcon><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg></SocialIcon>
                            </button>
                            <div className="relative">
                                <button onClick={shareNative} data-testid="share-native" aria-label="Compartir">
                                    <SocialIcon><Share2 className="h-4 w-4" /></SocialIcon>
                                </button>
                                <SharePanel
                                    url={publicUrl}
                                    title={`@${nickname} en Ok te lo vendo`}
                                    isOpen={shareNativePanelOpen}
                                    onClose={closeShareNativePanel}
                                    className="bottom-full mb-2 right-0"
                                />
                            </div>
                            <button onClick={() => setQrOpen(true)} data-testid="share-qr" className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-black/50 border border-white/20 hover:bg-black/70 transition-colors text-sm font-medium">
                                <QrCode className="h-4 w-4" /> Compartir con QR
                            </button>
                            <button onClick={copyLink} data-testid="share-copy" className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 transition-colors text-sm">
                                <Copy className="h-4 w-4" /> Copiar enlace
                            </button>
                            {hasStorefront && (
                                <Link to={`/store/${nickname}`} className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors text-sm font-semibold" data-testid="view-storefront-btn">
                                    <ShoppingBag className="h-4 w-4" /> Ver tienda
                                </Link>
                            )}
                            {(!me || me?.id !== u.id) && (
                                <>
                                    <button onClick={toggleFollow} data-testid="follow-button" className={`inline-flex items-center gap-2 h-9 px-4 rounded-full text-sm font-medium transition-colors ${following ? 'bg-white/10 border border-white/40 text-white hover:bg-white/20' : 'bg-white text-black hover:bg-white/90'}`}>
                                        {following ? <><UserCheck className="h-4 w-4" /> Siguiendo</> : <><UserPlus className="h-4 w-4" /> Seguir</>}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            if (role !== 'user') {
                                                const ok = await promptLogin({ intent: 'comment' });
                                                if (!ok) return;
                                            }
                                            // Try to open OkChat with a dedicated room for this buyer↔seller conversation
                                            if (typeof window.okChatOpen === 'function') {
                                                try {
                                                    const { data: roomData } = await api.post('/okchat/room', {
                                                        seller_id: u.id,
                                                    });
                                                    window.okChatOpen({ room: { id: roomData.room_id, name: roomData.room_name } });
                                                    return;
                                                } catch {
                                                    // OkChat not configured — fall back to legacy ChatDrawer
                                                }
                                            }
                                            setChatOpen(true);
                                        }}
                                        data-testid="pub-profile-message"
                                        className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-white/10 border border-white/40 text-white hover:bg-white/20 transition-colors text-sm font-medium"
                                    >
                                        <MessageCircle className="h-4 w-4" /> Mensaje
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </section>

                <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-8">
                    <Tabs defaultValue="ads">
                        <TabsList>
                            <TabsTrigger value="ads" data-testid="profile-tab-ads">Anuncios ({data.ads.length})</TabsTrigger>
                            <TabsTrigger value="reviews" data-testid="profile-tab-reviews">Calificaciones ({data.ratings.length})</TabsTrigger>
                        </TabsList>
                        <TabsContent value="ads" className="mt-4">
                            {/* Filters */}
                            <div className="flex flex-wrap items-center gap-2 mb-5">
                                <Input placeholder="Buscar en sus anuncios..." value={q} onChange={(e) => setQ(e.target.value)} className="w-full sm:w-64" data-testid="profile-search" />
                                <Select value={category} onValueChange={setCategory}>
                                    <SelectTrigger className="w-40" data-testid="profile-filter-cat"><SelectValue placeholder="Categoría" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas</SelectItem>
                                        {cats.map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                <Select value={condition} onValueChange={setCondition}>
                                    <SelectTrigger className="w-36" data-testid="profile-filter-cond"><SelectValue placeholder="Condición" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Todas</SelectItem>
                                        <SelectItem value="nuevo">Nuevo</SelectItem>
                                        <SelectItem value="seminuevo">Seminuevo</SelectItem>
                                        <SelectItem value="usado">Usado</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={sort} onValueChange={setSort}>
                                    <SelectTrigger className="w-36" data-testid="profile-sort"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="recent">Más recientes</SelectItem>
                                        <SelectItem value="price_asc">Precio ↑</SelectItem>
                                        <SelectItem value="price_desc">Precio ↓</SelectItem>
                                        <SelectItem value="popular">Más vistos</SelectItem>
                                    </SelectContent>
                                </Select>
                                <span className="text-xs text-muted-foreground ml-auto">{sorted.length} resultado(s)</span>
                            </div>
                            {sorted.length === 0 ? (
                                <div className="text-center py-16 text-muted-foreground">Sin resultados.</div>
                            ) : (
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5" data-testid="profile-ads-grid">
                                    {sorted.map(a => <AdCard key={a.id} ad={a} />)}
                                </div>
                            )}
                        </TabsContent>
                        <TabsContent value="reviews" className="mt-4">
                            {/* OKCalifícalo header */}
                            <div className="flex items-center gap-3 mb-4">
                                <img src="/assets/okcalificalo-logo.png" alt="OKCalifícalo" className="h-8 w-auto" onError={e => { e.target.style.display='none'; }} />
                                <span className="text-xs text-muted-foreground">Sistema de calificaciones OKCalifícalo</span>
                            </div>

                            {/* Satisfaction banner */}
                            {(() => {
                                const total = data.ratings.length;
                                const avg = total > 0 ? (data.ratings.reduce((s, r) => s + r.stars, 0) / total).toFixed(1) : '0.0';
                                const dist = {1:0,2:0,3:0,4:0,5:0};
                                data.ratings.forEach(r => { dist[r.stars] = (dist[r.stars] || 0) + 1; });
                                const positiveCount = (dist[4] || 0) + (dist[5] || 0);
                                const satisfaction = total > 0 ? Math.round((positiveCount / total) * 100) : 0;
                                const satColor = satisfaction >= 80 ? '#10b981' : satisfaction >= 60 ? '#f59e0b' : '#ef4444';
                                const circumference = 2 * Math.PI * 42;
                                const dashOffset = circumference - (satisfaction / 100) * circumference;
                                return (
                                    <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-neutral-900 text-white p-5 mb-6">
                                        <div className="flex flex-col sm:flex-row gap-6 items-center">
                                            {/* Score circle */}
                                            <div className="shrink-0 flex flex-col items-center gap-3">
                                                <div className="relative h-28 w-28">
                                                    <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100" width="112" height="112">
                                                        <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                                                        <circle cx="50" cy="50" r="42" fill="none" stroke={satColor}
                                                            strokeWidth="8" strokeLinecap="round"
                                                            strokeDasharray={circumference}
                                                            strokeDashoffset={dashOffset}
                                                            style={{ transition: 'stroke-dashoffset 0.8s ease' }}
                                                        />
                                                    </svg>
                                                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                                                        <span className="text-2xl font-bold tabular-nums">{satisfaction}%</span>
                                                        <span className="text-[10px] text-white/60 uppercase tracking-wide mt-0.5">Satis.</span>
                                                    </div>
                                                </div>
                                                <div className="text-center">
                                                    <div className="text-3xl font-bold tabular-nums leading-none">{avg}</div>
                                                    <RatingStars value={parseFloat(avg)} size="sm" />
                                                    <div className="text-xs text-white/50 mt-1">({total} {total === 1 ? 'calificación' : 'calificaciones'})</div>
                                                </div>
                                            </div>
                                            {/* Distribution bars */}
                                            <div className="flex-1 w-full space-y-2">
                                                {[5,4,3,2,1].map(star => {
                                                    const count = dist[star] || 0;
                                                    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                                                    return (
                                                        <div key={star} className="flex items-center gap-2">
                                                            <div className="w-5 text-[11px] text-right text-white/60 tabular-nums">{star}</div>
                                                            <Star className="h-3 w-3 text-amber-400 fill-amber-400 shrink-0" />
                                                            <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                                                                <div className={`h-full rounded-full ${star >= 4 ? 'bg-emerald-400' : star === 3 ? 'bg-amber-400' : 'bg-red-400'}`}
                                                                    style={{ width: `${pct}%`, transition: 'width 0.8s ease' }} />
                                                            </div>
                                                            <div className="w-5 text-[11px] text-white/50 tabular-nums">{count}</div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        {eligibleOrders.length > 0 && (
                                            <div className="mt-4 flex justify-end border-t border-white/10 pt-4">
                                                <button onClick={() => setRatingOpen(true)}
                                                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white text-slate-900 font-semibold text-sm hover:bg-white/90 transition-colors shadow-lg"
                                                    data-testid="calificar-vendedor">
                                                    <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
                                                    Calificar vendedor
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                            {/* Timing stats charts */}
                            {timingStats && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                                    {/* Shipping time */}
                                    <div className="rounded-2xl border bg-card p-4">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="h-8 w-8 rounded-xl bg-blue-100 flex items-center justify-center">
                                                <Truck className="h-4 w-4 text-blue-600" />
                                            </div>
                                            <div>
                                                <div className="text-sm font-semibold">¿Cuánto tardan sus envíos?</div>
                                                <div className="text-[11px] text-muted-foreground">{timingStats.sample_size > 0 ? `Basado en ${timingStats.sample_size} pedidos` : 'Sin datos aún'}</div>
                                            </div>
                                        </div>
                                        {timingStats.avg_ship_days !== null ? (() => {
                                            const d = timingStats.avg_ship_days;
                                            const isfast = d <= 3;
                                            const isMed = d <= 7;
                                            const color = isfast ? 'bg-emerald-500' : isMed ? 'bg-amber-500' : 'bg-red-500';
                                            const label = isfast ? 'Envío rápido' : isMed ? 'Entrega normal' : 'Entrega lenta';
                                            const pct = Math.min(100, Math.round((1 - (d - 1) / 30) * 100));
                                            return (
                                                <div>
                                                    <div className="text-3xl font-bold tabular-nums mb-1">{d}<span className="text-base font-normal text-muted-foreground"> días</span></div>
                                                    <div className="text-xs text-muted-foreground mb-2">{label} en promedio</div>
                                                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                                                        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${Math.max(5, pct)}%` }} />
                                                    </div>
                                                </div>
                                            );
                                        })() : (
                                            <div className="text-sm text-muted-foreground py-2">Sin ventas completadas aún.</div>
                                        )}
                                    </div>

                                    {/* Response time / completions */}
                                    <div className="rounded-2xl border bg-card p-4">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="h-8 w-8 rounded-xl bg-purple-100 flex items-center justify-center">
                                                <Clock className="h-4 w-4 text-purple-600" />
                                            </div>
                                            <div>
                                                <div className="text-sm font-semibold">¿Cuánto tarda en contestar?</div>
                                                <div className="text-[11px] text-muted-foreground">Historial de respuesta al comprador</div>
                                            </div>
                                        </div>
                                        <div className="flex items-end gap-4">
                                            <div>
                                                <div className="text-3xl font-bold tabular-nums">{timingStats.completed_orders}</div>
                                                <div className="text-xs text-muted-foreground">pedidos completados</div>
                                            </div>
                                            {timingStats.completed_orders > 0 && (
                                                <div className="text-xs text-emerald-600 font-medium flex items-center gap-1 mb-1">
                                                    <CheckCircle2 className="h-3.5 w-3.5" />Vendedor activo
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-[11px] text-muted-foreground mt-2">
                                            El tiempo de respuesta exacto estará disponible próximamente.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Rating cards */}
                            {data.ratings.length === 0 ? (
                                <div className="text-sm text-muted-foreground text-center py-6">Aún sin calificaciones de usuarios.</div>
                            ) : (
                                <div className="space-y-3">
                                    {data.ratings.map(r => (
                                        <div key={r.id} className="rounded-2xl border bg-card p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-8 w-8 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-semibold text-sm">{r.rater?.nickname?.[0]?.toUpperCase() || '?'}</div>
                                                    <div className="text-sm font-medium">@{r.rater?.nickname}</div>
                                                </div>
                                                <RatingStars value={r.stars} size="md" />
                                            </div>
                                            {r.comment && <p className="mt-2 text-sm text-foreground/85">{r.comment}</p>}
                                            <div className="mt-1.5 text-[11px] text-muted-foreground">{new Date(r.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    </Tabs>
                </div>
            </main>
            

            {/* Seller rating dialog */}
            <Dialog open={ratingOpen} onOpenChange={setRatingOpen}>
                <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-sm" data-testid="rating-modal">
                    <DialogHeader>
                        <div className="flex items-center justify-center pb-2">
                            <img src="/assets/okcalificalo-logo.png" alt="OKCalifícalo" className="h-10 w-auto" />
                        </div>
                        <DialogTitle className="text-center">Calificar a @{u.nickname}</DialogTitle>
                        <DialogDescription className="text-center text-xs">Solo compradores con pedidos completados pueden calificar.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        {eligibleOrders.length > 1 && (
                            <Select value={selectedOrderId} onValueChange={setSelectedOrderId}>
                                <SelectTrigger data-testid="rating-order-select"><SelectValue placeholder="Selecciona una compra" /></SelectTrigger>
                                <SelectContent>
                                    {eligibleOrders.map(o => (
                                        <SelectItem key={o.id} value={o.id}>
                                            {o.ad?.title || 'Pedido'} — {new Date(o.created_at).toLocaleDateString('es-MX')}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        <div className="flex items-center justify-center gap-1">
                            {[1,2,3,4,5].map(n => (
                                <button key={n} type="button" onClick={() => setRatingStars(n)}
                                    className={`h-10 w-10 text-3xl transition-transform hover:scale-110 ${n <= ratingStars ? 'text-amber-500' : 'text-neutral-300'}`}
                                    data-testid={`rating-star-${n}`}>★</button>
                            ))}
                            <span className="ml-2 text-sm text-muted-foreground">{ratingStars}/5</span>
                        </div>
                        <Textarea
                            placeholder="Comparte tu experiencia con este vendedor…"
                            value={ratingComment}
                            onChange={e => setRatingComment(e.target.value)}
                            rows={3}
                            maxLength={500}
                            data-testid="rating-comment"
                        />
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setRatingOpen(false)}>Cancelar</Button>
                        <Button onClick={submitUserRating} disabled={ratingBusy} className="bg-red-600 hover:bg-red-700" data-testid="rating-submit">
                            {ratingBusy ? 'Publicando…' : 'Publicar calificación'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={qrOpen} onOpenChange={setQrOpen}>
                <DialogContent
                    className="w-[calc(100vw-2rem)] sm:max-w-sm max-h-[90vh] overflow-y-auto p-4 sm:p-6"
                    data-testid="user-qr-modal"
                >
                    <DialogHeader>
                        <DialogTitle>Compartir con QR</DialogTitle>
                    </DialogHeader>
                    <div className="flex flex-col items-center gap-3 sm:gap-4 py-2 sm:py-4">
                        {/* User avatar + info */}
                        <div className="flex flex-col items-center gap-2">
                            <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-full ring-4 ring-red-600 overflow-hidden bg-neutral-100 shadow-lg" data-testid="user-qr-avatar">
                                {u.avatar_url ? (
                                    <img src={u.avatar_url} alt={u.nickname} className="h-full w-full object-cover" />
                                ) : (
                                    <div className="h-full w-full flex items-center justify-center text-2xl font-bold bg-red-600 text-white">{u.nickname[0]?.toUpperCase()}</div>
                                )}
                            </div>
                            <div className="text-center">
                                <div className="font-semibold text-base sm:text-lg">@{u.nickname}</div>
                                {u.location && <div className="text-xs text-muted-foreground">{u.location}</div>}
                            </div>
                        </div>
                        <div className="p-3 sm:p-4 bg-white rounded-2xl border-2 border-red-600" data-testid="qr-code-container">
                            <QRCodeSVG
                                value={publicUrl}
                                size={typeof window !== 'undefined' && window.innerWidth < 480 ? 160 : 220}
                                level="M"
                                includeMargin={false}
                                fgColor="#dc2626"
                            />
                        </div>
                        <p className="text-[11px] sm:text-xs text-center text-muted-foreground break-all px-2">{publicUrl}</p>
                        <Button onClick={copyLink} variant="outline" className="w-full" data-testid="user-qr-copy"><Copy className="h-4 w-4 mr-2" />Copiar enlace</Button>
                    </div>
                </DialogContent>
            </Dialog>
            {data?.user?.id && (
                <ChatDrawer
                    open={chatOpen}
                    onOpenChange={setChatOpen}
                    mode="direct"
                    toUserId={data.user.id}
                    counterpartLabel={data.user.nickname}
                    title={`Chat con @${data.user.nickname}`}
                />
            )}
        </div>
    );
}
